import { useState } from "react";
import { InputLookup } from "../inputs/InputLookup";
import type { Input as InventoryInput } from "../../services/inputsApi";
import { useCreateRequisition } from "../../hooks/useRequisition";
import type { RequisitionItem } from "../../services/requisitionApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Check, ChevronsUpDown, Trash2, Plus } from "lucide-react";
import { useGreenhouses } from "@/features/farms/hooks/useGreenhouses";
import { useApiError } from "@/shared/hooks/useApiError";
import { useNotification } from "@/shared/hooks/useNotification";
import { useFieldErrorActions } from "@/shared/contexts/field-error-context";
import { FieldError } from "@/shared/components/forms/FieldError";
import { FormErrorSummary } from "@/shared/components/forms/FormErrorSummary";
import { useDepartments } from "@/shared/hooks/useDepartments";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { useOrders } from "@/features/production/hooks/useOrders";

interface FormItem extends RequisitionItem {
  tempId: string;
}

interface FormData {
  requiredByDate: string;
  purpose: string;
  departmentId: string;
  greenhouseId: string;
  farmId: string;
  fieldId: string;
  notes: string;
}

const FORM_ID = "createRequisition";

const initialFormData = (defaultDeptId: string): FormData => ({
  requiredByDate: "",
  purpose: "",
  departmentId: defaultDeptId,
  greenhouseId: "",
  farmId: "",
  fieldId: "",
  notes: "",
});

const initialItemData = {
  inputId: "",
  quantityRequested: "",
  unitOfMeasure: "",
  orderId: "",
  orderItemId: "",
  sowingInstructionId: "",
};

export const RequisitionForm = () => {
  const { user } = useAuthStore();
  const defaultDeptId = user?.departmentId ?? "";
  const [formData, setFormData] = useState<FormData>(initialFormData(defaultDeptId));
  const [itemData, setItemData] = useState(initialItemData);
  const [items, setItems] = useState<FormItem[]>([]);
  const [selectedInput, setSelectedInput] = useState<InventoryInput | null>(null);
  const [inputDetails, setInputDetails] = useState<Record<string, InventoryInput>>({});
  const [orderOpen, setOrderOpen] = useState(false);
  const [orderItemOpen, setOrderItemOpen] = useState(false);
  const [sowingInstructionOpen, setSowingInstructionOpen] = useState(false);

  const departmentsQuery = useDepartments();
  const greenhousesQuery = useGreenhouses();
  const ordersQuery = useOrders();
  const createRequisitionMutation = useCreateRequisition();
  const handleApiError = useApiError();
  const notify = useNotification();
  const { clearField, clearForm } = useFieldErrorActions();


  const orders = ordersQuery.data ?? [];
  const selectedOrder = orders.find((o) => o.id === itemData.orderId);
  const selectedOrderItem = selectedOrder?.orderItems.find(
    (oi) => oi.id === itemData.orderItemId
  );
  const sowingInstructions = selectedOrderItem?.sowingInstructions ?? [];

  const handleFormChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    clearField(FORM_ID, field);
  };

  const handleItemChange = (
    field: keyof typeof initialItemData,
    value: string
  ) => {
    setItemData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddItem = () => {
    if (!itemData.inputId || !itemData.quantityRequested) {
      notify.warning("Please fill in input and quantity");
      return;
    }

    const newItem: FormItem = {
      tempId: Date.now().toString(),
      inputId: itemData.inputId,
      quantityRequested: parseFloat(itemData.quantityRequested),
      unitOfMeasure: itemData.unitOfMeasure || selectedInput?.unitOfMeasure || "",
      orderId: itemData.orderId || undefined,
      orderItemId: itemData.orderItemId || undefined,
      sowingInstructionId: itemData.sowingInstructionId || undefined,
    };

    setItems([...items, newItem]);
    setItemData(initialItemData);
    setSelectedInput(null);
  };

  const handleRemoveItem = (tempId: string) => {
    setItems(items.filter((i) => i.tempId !== tempId));
  };

  const handleSubmit = () => {
    if (!formData.requiredByDate || !formData.purpose || !formData.departmentId) {
      notify.warning("Please fill in all required fields");
      return;
    }

    if (items.length === 0) {
      notify.warning("Please add at least one item");
      return;
    }

    const payload = {
      requiredByDate: formData.requiredByDate,
      purpose: formData.purpose,
      departmentId: formData.departmentId,
      greenhouseId: formData.greenhouseId || undefined,
      farmId: formData.farmId || undefined,
      fieldId: formData.fieldId || undefined,
      notes: formData.notes,
      items: items.map(({ tempId, ...rest }) => rest),
    };

    createRequisitionMutation.mutate(payload as any, {
      onSuccess: () => {
        clearForm(FORM_ID);
        setFormData(initialFormData(defaultDeptId));
        setItems([]);
        notify.success("Requisition submitted successfully");
      },
      onError: (error) => handleApiError(error, { formId: FORM_ID }),
    });
  };

  return (
    <div className="space-y-6">
      {/* Main Form */}
      <div className="rounded-lg border p-6 space-y-6">
        <h2 className="text-xl font-semibold">Create Requisition</h2>

        <FormErrorSummary formId={FORM_ID} />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
          <Field>
            <FieldLabel htmlFor="requiredByDate">
              Required By Date <span className="text-red-500">*</span>
            </FieldLabel>
            <DatePicker
              id="requiredByDate"
              value={formData.requiredByDate}
              onChange={(value) => handleFormChange("requiredByDate", value)}
              disabled={createRequisitionMutation.isPending}
            />
            <FieldError formId={FORM_ID} field="requiredByDate" />
          </Field>

          <Field>
            <FieldLabel htmlFor="department">
              Department <span className="text-red-500">*</span>
            </FieldLabel>
            <Select
              value={formData.departmentId}
              onValueChange={(value) => handleFormChange("departmentId", value)}
              disabled={createRequisitionMutation.isPending}
            >
              <SelectTrigger id="department" className="w-full">
                <SelectValue placeholder="Select Department" />
              </SelectTrigger>
              <SelectContent>
                {departmentsQuery.data?.map((dept) => (
                  <SelectItem key={dept.id} value={dept.id}>
                    {dept.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldError formId={FORM_ID} field="departmentId" />
          </Field>

          <Field>
            <FieldLabel htmlFor="greenhouseId">Greenhouse</FieldLabel>
            <Select
              value={formData.greenhouseId}
              onValueChange={(value) =>
                handleFormChange("greenhouseId", value)
              }
              disabled={createRequisitionMutation.isPending}
            >
              <SelectTrigger id="greenhouseId" className="w-full">
                <SelectValue placeholder="Select Greenhouse" />
              </SelectTrigger>
              <SelectContent>
                {greenhousesQuery.data?.map((gh) => (
                  <SelectItem key={gh.id} value={gh.id}>
                    {gh.greenhouseName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldError formId={FORM_ID} field="greenhouseId" />
          </Field>

          <Field>
            <FieldLabel htmlFor="farmId">Farm</FieldLabel>
            <Input
              id="farmId"
              placeholder="Farm ID (optional)"
              value={formData.farmId}
              onChange={(e) => handleFormChange("farmId", e.target.value)}
              disabled={createRequisitionMutation.isPending}
            />
            <FieldError formId={FORM_ID} field="farmId" />
          </Field>

          <Field>
            <FieldLabel htmlFor="fieldId">Field</FieldLabel>
            <Input
              id="fieldId"
              placeholder="Field ID (optional)"
              value={formData.fieldId}
              onChange={(e) => handleFormChange("fieldId", e.target.value)}
              disabled={createRequisitionMutation.isPending}
            />
            <FieldError formId={FORM_ID} field="fieldId" />
          </Field>
        </div>

        <Field>
          <FieldLabel htmlFor="purpose">
            Purpose <span className="text-red-500">*</span>
          </FieldLabel>
          <Textarea
            id="purpose"
            value={formData.purpose}
            onChange={(e) => handleFormChange("purpose", e.target.value)}
            placeholder="What is this requisition for?"
            disabled={createRequisitionMutation.isPending}
          />
          <FieldError formId={FORM_ID} field="purpose" />
        </Field>

        <Field>
          <FieldLabel htmlFor="notes">Notes</FieldLabel>
          <Textarea
            id="notes"
            value={formData.notes}
            onChange={(e) => handleFormChange("notes", e.target.value)}
            placeholder="Additional notes"
            disabled={createRequisitionMutation.isPending}
          />
          <FieldError formId={FORM_ID} field="notes" />
        </Field>
      </div>

      {/* Add Items */}
      <div className="rounded-lg border p-6 space-y-4">
        <h2 className="text-xl font-semibold">Add Items</h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field>
            <FieldLabel htmlFor="inputId">
              Input <span className="text-red-500">*</span>
            </FieldLabel>
            <InputLookup
              value={itemData.inputId}
              selectedInput={selectedInput}
              onSelect={(input) => {
                handleItemChange("inputId", input.id);
                setSelectedInput(input);
                setInputDetails((previous) => ({ ...previous, [input.id]: input }));
              }}
              disabled={createRequisitionMutation.isPending}
            />
            {selectedInput && (
              <p
                className={
                  (selectedInput.currentStock ?? 0) > 0
                    ? "text-xs text-muted-foreground mt-1"
                    : "text-xs text-amber-700 mt-1"
                }
              >
                {(selectedInput.currentStock ?? 0) > 0
                  ? `In stock: ${selectedInput.currentStock} ${
                      selectedInput.unitOfMeasureDisplay ||
                      selectedInput.unitOfMeasure
                    }`
                  : `Out of stock — store manager will need to procure externally.`}
              </p>
            )}
          </Field>

          <Field>
            <FieldLabel htmlFor="quantityRequested">
              Quantity <span className="text-red-500">*</span>
            </FieldLabel>
            <Input
              id="quantityRequested"
              type="number"
              step="0.01"
              value={itemData.quantityRequested}
              onChange={(e) =>
                handleItemChange("quantityRequested", e.target.value)
              }
              disabled={createRequisitionMutation.isPending}
            />
            {selectedInput && itemData.quantityRequested && (() => {
              const qty = parseFloat(itemData.quantityRequested);
              const stock = selectedInput.currentStock ?? 0;
              if (isNaN(qty) || qty <= 0) return null;
              if (qty > stock) {
                return (
                  <p className="text-xs text-amber-700 mt-1">
                    Exceeds stock by {(qty - stock).toFixed(2)} — store will
                    issue what it has and may need to procure the rest.
                  </p>
                );
              }
              return null;
            })()}
          </Field>

          <Field>
            <FieldLabel htmlFor="unitOfMeasure">Unit</FieldLabel>
            <Input
              id="unitOfMeasure"
              value={
                selectedInput?.unitOfMeasureDisplay ||
                selectedInput?.unitOfMeasure ||
                ""
              }
              readOnly
              disabled={createRequisitionMutation.isPending}
              placeholder="Auto-filled"
            />
          </Field>
        </div>

        {/* Optional Fields — Order linking */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
          <Field>
            <FieldLabel>Order (optional)</FieldLabel>
            <Popover open={orderOpen} onOpenChange={setOrderOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={orderOpen}
                  className="w-full justify-between font-normal"
                  disabled={ordersQuery.isLoading || createRequisitionMutation.isPending}
                >
                  {selectedOrder
                    ? `${selectedOrder.orderNumber} — ${selectedOrder.customerName}`
                    : ordersQuery.isLoading
                      ? "Loading…"
                      : "Search order…"}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Type order number or customer…" />
                  <CommandList>
                    <CommandEmpty>No orders found</CommandEmpty>
                    <CommandGroup>
                      {orders.map((o) => (
                        <CommandItem
                          key={o.id}
                          value={`${o.orderNumber} ${o.customerName}`}
                          onSelect={() => {
                            const newId = o.id === itemData.orderId ? "" : o.id;
                            handleItemChange("orderId", newId);
                            handleItemChange("orderItemId", "");
                            handleItemChange("sowingInstructionId", "");
                            setOrderOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              itemData.orderId === o.id ? "opacity-100" : "opacity-0"
                            )}
                          />
                          {o.orderNumber} — {o.customerName}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </Field>

          <Field>
            <FieldLabel>Order Item (optional)</FieldLabel>
            <Popover open={orderItemOpen} onOpenChange={setOrderItemOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={orderItemOpen}
                  className="w-full justify-between font-normal"
                  disabled={!selectedOrder || createRequisitionMutation.isPending}
                >
                  {selectedOrderItem
                    ? `${selectedOrderItem.productName}${selectedOrderItem.variety ? ` (${selectedOrderItem.variety})` : ""}`
                    : selectedOrder
                      ? "Select item…"
                      : "Select order first"}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search product…" />
                  <CommandList>
                    <CommandEmpty>No items</CommandEmpty>
                    <CommandGroup>
                      {(selectedOrder?.orderItems ?? []).map((oi) => (
                        <CommandItem
                          key={oi.id}
                          value={`${oi.productName} ${oi.variety ?? ""}`}
                          onSelect={() => {
                            const newId = oi.id === itemData.orderItemId ? "" : oi.id;
                            handleItemChange("orderItemId", newId);
                            handleItemChange("sowingInstructionId", "");
                            setOrderItemOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              itemData.orderItemId === oi.id ? "opacity-100" : "opacity-0"
                            )}
                          />
                          {oi.productName}{oi.variety ? ` (${oi.variety})` : ""} — Qty {oi.quantity}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </Field>

          <Field>
            <FieldLabel>Sowing Instruction (optional)</FieldLabel>
            <Popover open={sowingInstructionOpen} onOpenChange={setSowingInstructionOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={sowingInstructionOpen}
                  className="w-full justify-between font-normal"
                  disabled={!selectedOrderItem || sowingInstructions.length === 0 || createRequisitionMutation.isPending}
                >
                  {itemData.sowingInstructionId
                    ? (() => {
                        const si = sowingInstructions.find((s) => s.id === itemData.sowingInstructionId);
                        return si ? si.instructionNumber : "Select…";
                      })()
                    : sowingInstructions.length === 0
                      ? "No instructions"
                      : "Select instruction…"}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search instruction…" />
                  <CommandList>
                    <CommandEmpty>No instructions</CommandEmpty>
                    <CommandGroup>
                      {sowingInstructions.map((si) => (
                        <CommandItem
                          key={si.id}
                          value={`${si.instructionNumber} ${si.status}`}
                          onSelect={() => {
                            const newId = si.id === itemData.sowingInstructionId ? "" : si.id;
                            handleItemChange("sowingInstructionId", newId);
                            setSowingInstructionOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              itemData.sowingInstructionId === si.id ? "opacity-100" : "opacity-0"
                            )}
                          />
                          {si.instructionNumber} — {si.status} (Qty {si.quantityToSow})
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </Field>
        </div>

        <Button
          onClick={handleAddItem}
          disabled={createRequisitionMutation.isPending}
          className="gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Item
        </Button>
      </div>

      {/* Items Table */}
      {items.length > 0 && (
        <div className="rounded-lg border p-6 space-y-4">
          <h2 className="text-xl font-semibold">Items ({items.length})</h2>

          <div className="rounded border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Input</TableHead>
                  <TableHead>Quantity</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => {
                  const input = item.inputId ? inputDetails[item.inputId] : undefined;
                  return (
                    <TableRow key={item.tempId}>
                      <TableCell>{input?.inputName ?? item.inputId}</TableCell>
                      <TableCell>{item.quantityRequested}</TableCell>
                      <TableCell>{input?.unitOfMeasureDisplay || input?.unitOfMeasure || item.unitOfMeasure}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveItem(item.tempId)}
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <div className="flex gap-2 justify-end">
            <Button
              variant="secondary"
              onClick={() => setItems([])}
              disabled={createRequisitionMutation.isPending}
            >
              Clear Items
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={createRequisitionMutation.isPending}
            >
              {createRequisitionMutation.isPending
                ? "Submitting..."
                : "Submit Requisition"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
